import {
  PlanType,
  TEAM_ACTIONS,
  TEAM_PERMISSIONS,
  TEAM_ROLES,
} from '@formo/shared';
import { FC, ReactElement, createContext, useMemo } from 'react';
import useDashboard from '~/app/hooks/useDashboard';
import useFormTable from '~/app/hooks/useFormTable';
import { useTeamTier } from '~/app/hooks/useTeamTier';
import {
  PLANS_MAX_FORM,
  PLANS_MAX_MONTHLY_EVENTS,
  PLANS_MAX_MONTHLY_USERS,
  PLANS_MAX_RESPONSES,
  PLANS_MAX_SEATS,
} from '~/constants/plans';
import dayjs from '~/utils/dayjs';

// Constants
export const RESTRICTION_TYPE = {
  SEAT: 'SEAT',
  EVENT: 'EVENT',
  MTU: 'MTU',
  FORM: 'FORM',
  RESPONSE: 'RESPONSE',
  WALLET_PROFILE: 'WALLET_PROFILE',
  ACTIVITY: 'ACTIVITY',
} as const;

export const USAGE_RESTRICTIONS = [
  RESTRICTION_TYPE.EVENT,
  RESTRICTION_TYPE.MTU,
  RESTRICTION_TYPE.RESPONSE,
] as const;

// Fixed values for specific limitations
const WALLET_PROFILE_MIN_PRIORITY = 1;
const ACTIVITY_HOURS_LIMIT = 24;

// Types
type RestrictionKeys = keyof typeof RESTRICTION_TYPE;
type RestrictionCheck = boolean | ((checkDate: string) => boolean);
type Restrictions = Record<RestrictionKeys, RestrictionCheck>;
type UsageLimitations = Record<RestrictionKeys, number>;

interface AccessGateContextType {
  hasAccessToBilling: boolean | null;
  meetFeaturesRestrictions: Restrictions | null;
  limitations: UsageLimitations | null;
  checkRestriction: (
    action: (activeRestrictions: (typeof USAGE_RESTRICTIONS)[number][]) => void,
  ) => void;
}

interface AccessGateProviderProps {
  children: ReactElement;
}

// Context
export const AccessGateContext = createContext<AccessGateContextType>({
  hasAccessToBilling: null,
  meetFeaturesRestrictions: null,
  limitations: null,
  checkRestriction: () => {},
});

// Access Check Logic
const checkAccess = (role?: TEAM_ROLES): boolean => {
  if (!role) return false;
  return TEAM_PERMISSIONS[role]?.includes(TEAM_ACTIONS.UPDATE_TIER) || false;
};

// Provider
const AccessGateProvider: FC<AccessGateProviderProps> = ({ children }) => {
  const { activeTeam, members } = useDashboard();
  const { activeForms } = useFormTable();
  const {
    activeTeamTier,
    currentEventsCount,
    currentUsersCount,
    currentResponsesCount,
  } = useTeamTier();

  const hasAccessToBilling = checkAccess(activeTeam?.role as TEAM_ROLES);
  const tierType = activeTeamTier.type as PlanType;

  const limitations: UsageLimitations = {
    [RESTRICTION_TYPE.SEAT]: PLANS_MAX_SEATS[tierType],
    [RESTRICTION_TYPE.EVENT]: PLANS_MAX_MONTHLY_EVENTS[tierType],
    [RESTRICTION_TYPE.MTU]: PLANS_MAX_MONTHLY_USERS[tierType],
    [RESTRICTION_TYPE.FORM]: PLANS_MAX_FORM[tierType],
    [RESTRICTION_TYPE.RESPONSE]: PLANS_MAX_RESPONSES[tierType],
    [RESTRICTION_TYPE.WALLET_PROFILE]: WALLET_PROFILE_MIN_PRIORITY,
    [RESTRICTION_TYPE.ACTIVITY]: ACTIVITY_HOURS_LIMIT,
  };

  const meetFeaturesRestrictions: Restrictions = {
    [RESTRICTION_TYPE.SEAT]:
      members.length >= limitations[RESTRICTION_TYPE.SEAT],
    [RESTRICTION_TYPE.EVENT]:
      currentEventsCount >= limitations[RESTRICTION_TYPE.EVENT],
    [RESTRICTION_TYPE.MTU]:
      currentUsersCount >= limitations[RESTRICTION_TYPE.MTU],
    [RESTRICTION_TYPE.FORM]:
      activeForms.length >= limitations[RESTRICTION_TYPE.FORM],
    [RESTRICTION_TYPE.RESPONSE]:
      currentResponsesCount >= limitations[RESTRICTION_TYPE.RESPONSE],
    [RESTRICTION_TYPE.WALLET_PROFILE]:
      (activeTeamTier?.priority || 0) <
      limitations[RESTRICTION_TYPE.WALLET_PROFILE],
    [RESTRICTION_TYPE.ACTIVITY]: (checkDate: string) =>
      dayjs
        .utc(checkDate)
        .local()
        .isBefore(
          dayjs()
            .local()
            .subtract(limitations[RESTRICTION_TYPE.ACTIVITY], 'hour'),
        ),
  };

  const checkRestriction = (
    action: (activeRestrictions: (typeof USAGE_RESTRICTIONS)[number][]) => void,
  ) => {
    const activeRestrictions = USAGE_RESTRICTIONS.filter(
      (type) => meetFeaturesRestrictions[type] === true,
    );

    if (activeRestrictions.length > 0) {
      action(activeRestrictions);
    }
  };

  const contextValue = useMemo(
    () => ({
      hasAccessToBilling,
      meetFeaturesRestrictions,
      limitations,
      checkRestriction,
    }),
    [
      hasAccessToBilling,
      meetFeaturesRestrictions,
      limitations,
      checkRestriction,
    ],
  );

  return (
    <AccessGateContext.Provider value={contextValue}>
      {children}
    </AccessGateContext.Provider>
  );
};

export default AccessGateProvider;
